import type { ReactNode } from 'react';
import { CHARACTERS } from '@/characters/definitions';
import { type CharacterExpression, getCharacterAsset } from '@/characters/manifest';
import { BbSubtitle } from '@/components/bb/BbSubtitle';
import { CardFace } from '@/components/cards/CardFace';
import { CharacterAvatar } from '@/components/character/CharacterAvatar';
import { BetChip } from '@/components/chips/BetChip';
import type { Card } from '@/engine/types';
import type { PersonalityName } from '@/gto/types';

export type OpponentSlotProps = {
  codename: PersonalityName;
  stack?: number;
  isTurn?: boolean;
  isFolded?: boolean;
  isAllIn?: boolean;
  isThinking?: boolean;
  /** Hole cards to reveal (replay learning mode or showdown). */
  holeCards?: readonly [Card, Card];
  /** Suppresses hole-card reveal even if `holeCards` is set. */
  hideHoleCards?: boolean;
  /** Chips this player has committed on the current street. */
  streetContribution?: number;
  /** Persistent "just acted" label with action verb (e.g., "raise 120"). */
  streetActionLabel?: string;
  /** Highlight as the pot winner (between-hands showdown). */
  isWinner?: boolean;
  /** Player is out of the tournament (stack hit 0). Stronger than folded —
   *  seat stays visible but is clearly marked OUT. */
  isEliminated?: boolean;
  /** Legacy prop — used to drive face-down card backs below the avatar.
   *  Now a no-op (backs removed from mobile layout) but kept so callers
   *  (`useTableView`, `HandReplayRoute`) don't need to drop the flag. */
  inHand?: boolean;
  /** BB size for unit conversion. 0 means "pre-hand / unknown → hide BB". */
  bbSize?: number;
  /** 0..4 seat index inside the ring. Drives alternating vertical offset
   *  for the overlay bubble so adjacent seats' bubbles don't stack at the
   *  same Y when both are populated (e.g. opp0 showing "RAISE 50" while
   *  opp1 is thinking). Odd seats ride higher than even seats. */
  seatIndex?: number;
  badge?: ReactNode;
};

function resolveExpression(state: {
  isFolded?: boolean;
  isAllIn?: boolean;
  isWinner?: boolean;
  isThinking?: boolean;
}): CharacterExpression {
  if (state.isWinner) return 'winner';
  if (state.isFolded) return 'folded';
  if (state.isAllIn) return 'allin';
  if (state.isThinking) return 'thinking';
  return 'idle';
}

/**
 * Opponent seat — fixed-width slot so bubbles and action labels never
 * push the avatar around. Everything that can appear/disappear mid-hand
 * (thinking bubble, action label, TURN arrow, bet chip) is absolutely
 * positioned relative to the slot container, so the avatar's x/y is the
 * sole anchor. Only the name + stack (+ hole cards at showdown) are in
 * flow beneath the avatar.
 */
export function OpponentSlot({
  codename,
  stack,
  isTurn,
  isFolded,
  isAllIn,
  isThinking,
  holeCards,
  hideHoleCards,
  streetContribution,
  streetActionLabel,
  isWinner,
  isEliminated,
  bbSize = 0,
  seatIndex = 0,
  badge,
}: OpponentSlotProps) {
  const def = CHARACTERS[codename];
  const asset = getCharacterAsset(codename);
  const expression = resolveExpression({
    isFolded: isFolded || isEliminated,
    isAllIn,
    isWinner,
    isThinking: isThinking && !isEliminated,
  });
  const dimmed = isEliminated
    ? 'opacity-20 grayscale contrast-75'
    : isFolded
      ? 'opacity-40 grayscale'
      : '';
  // Eliminated seats suppress live-state overlays — no thinking / action
  // label / turn arrow should flash on a dead seat.
  const hasOverlay = !isEliminated && (isTurn || streetActionLabel || isThinking);
  return (
    <div
      className={`relative mx-auto flex flex-col items-center gap-1 w-[clamp(48px,17vw,88px)] ${dimmed}`}
      data-testid={`opponent-${codename}`}
      data-turn={isTurn ? 'true' : undefined}
      data-folded={isFolded ? 'true' : undefined}
      data-allin={isAllIn ? 'true' : undefined}
      data-winner={isWinner ? 'true' : undefined}
      data-eliminated={isEliminated ? 'true' : undefined}
    >
      {/* Overlay zone ABOVE the avatar — single-item priority cascade
          (thinking > streetActionLabel > turn arrow). Showing only one
          at a time keeps the vertical footprint small.
          Odd-seat offset `mb-7` lifts seats 1/3 clearly higher than
          0/2/4, so neighboring overlays land at distinct Y levels and
          can each use their intrinsic (wider) content width — no
          need to truncate raise amounts. */}
      {hasOverlay ? (
        <div
          className={`absolute bottom-full ${seatIndex % 2 === 1 ? 'mb-7' : 'mb-1'} left-1/2 -translate-x-1/2 flex items-center justify-center pointer-events-none z-20 w-max`}
        >
          {isThinking ? (
            <div
              className="relative inline-flex items-center gap-1 bg-bg-elev border-2 border-accent-alt/70 rounded-sm px-1.5 py-0.5 shadow-md after:content-[''] after:absolute after:top-full after:left-1/2 after:-translate-x-1/2 after:border-x-[5px] after:border-x-transparent after:border-t-[6px] after:border-t-bg-elev"
              data-testid={`opponent-thinking-${codename}`}
              role="status"
              aria-label={`${def.displayName} 생각 중`}
            >
              <span aria-hidden="true" className="text-xs leading-none">
                🤔
              </span>
              <span className="flex gap-0.5" aria-hidden="true">
                <span
                  className="w-1 h-1 rounded-full bg-accent-alt animate-pulse"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="w-1 h-1 rounded-full bg-accent-alt animate-pulse"
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className="w-1 h-1 rounded-full bg-accent-alt animate-pulse"
                  style={{ animationDelay: '300ms' }}
                />
              </span>
            </div>
          ) : streetActionLabel ? (
            <span
              className="font-display text-[9px] bg-bg-elev text-accent-alt border-2 border-accent-alt/60 rounded-sm px-1.5 py-0.5 uppercase tracking-wider shadow-[0_0_6px_hsl(var(--color-accent-alt)/0.35)] whitespace-nowrap"
              data-testid={`opponent-last-${codename}`}
            >
              {streetActionLabel}
            </span>
          ) : isTurn ? (
            <span
              className="font-display text-[9px] bg-bg-elev text-accent-alt border border-accent-alt/50 rounded-sm px-1 leading-none flex items-center gap-0.5 text-glow-cyan tracking-wider animate-pulse whitespace-nowrap"
              data-testid={`opponent-arrow-${codename}`}
            >
              <span aria-hidden="true">▼</span>
              <span>TURN</span>
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Avatar anchor — ALL-IN / DEALER / WINNER badges tuck into its corners.
          The avatar tracks slot width responsively: clamp(42, 15vw, 60) so
          it never exceeds the slot (clamp(48, 17vw, 88)) on narrow
          viewports. `!` important wins over CharacterAvatar's internal
          SIZE map (md = w-16 h-16 = 64px). */}
      <div className="relative">
        <CharacterAvatar
          codename={asset.codename}
          expression={expression}
          size="md"
          active={isTurn && !isFolded}
          winner={isWinner}
          muted={isFolded}
          className="!w-[clamp(42px,15vw,60px)] !h-[clamp(42px,15vw,60px)]"
        />
        {isEliminated ? (
          <span
            className="absolute inset-0 z-20 flex items-center justify-center text-[11px] font-display font-bold text-danger bg-bg-deeper/70 rounded-pill border-2 border-danger/70 tracking-[0.3em]"
            data-testid={`opponent-eliminated-${codename}`}
          >
            OUT
          </span>
        ) : isAllIn ? (
          <span className="absolute -top-1 -right-1 z-10 text-[9px] font-display bg-chip-gold text-bg-deeper rounded-sm px-1.5 py-0.5 font-bold shadow-[0_0_8px_hsl(var(--color-warning)/0.8)]">
            ALL-IN
          </span>
        ) : null}
        {isWinner ? (
          <span
            role="img"
            aria-label="winner"
            className="absolute -top-5 left-1/2 -translate-x-1/2 text-2xl z-10"
            data-testid={`opponent-winner-${codename}`}
          >
            🏆
          </span>
        ) : null}
      </div>

      {/* Name + stack + BB — in flow, stable height. */}
      <span className="font-display text-[10px] tracking-wider text-fg uppercase leading-none max-w-full truncate">
        {def.displayName}
      </span>
      <div className="flex flex-col items-center leading-none gap-0.5">
        <span className="num text-sm text-muted">{stack ?? 0}</span>
        <BbSubtitle chips={stack ?? 0} bbSize={bbSize} tone="muted" />
      </div>

      {/* Bet chip — absolute below the slot so its show/fly animation
          doesn't affect flow height of the seat. */}
      <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 pointer-events-none flex flex-col items-center gap-0.5">
        <BetChip
          amount={streetContribution ?? 0}
          variant="opponent"
          testId={`opponent-bet-${codename}`}
        />
        {streetContribution && streetContribution > 0 ? (
          <BbSubtitle chips={streetContribution} bbSize={bbSize} tone="warning" />
        ) : null}
      </div>

      {/* Hole cards beneath the avatar:
           - Showdown + revealed: render face-up (flip animation).
           - Active hand: no card backs rendered. Mobile space is too
             tight for six extra card tiles hanging under every seat;
             the avatar + active-turn ring conveys "this seat is in
             the hand" without the chrome.
           - Folded / eliminated: nothing (muck). */}
      {!isEliminated && !isFolded && holeCards && !hideHoleCards ? (
        <div
          className="absolute top-full mt-1 left-1/2 -translate-x-1/2 flex gap-1"
          data-testid={`opponent-holecards-${codename}`}
        >
          <CardFace card={holeCards[0]} size="sm" />
          <CardFace card={holeCards[1]} size="sm" />
        </div>
      ) : null}

      {badge}
    </div>
  );
}
